import React, { useEffect, useRef } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import { GalleryImage } from "./ImageGallery";
import { useRecordImageView } from "../../hooks/useViews";
import { trackLightboxView } from "../../utils/analytics";

interface LightboxViewerProps {
  images: GalleryImage[];
  open: boolean;
  index: number;
  onClose: () => void;
  galleryId?: string;
}

export const LightboxViewer: React.FC<LightboxViewerProps> = ({
  images,
  open,
  index,
  onClose,
  galleryId,
}) => {
  const recordImageView = useRecordImageView();
  const viewedImages = useRef<Set<string>>(new Set());

  // Record view when lightbox opens or index changes
  useEffect(() => {
    if (open && images[index]?.id) {
      const imageId = images[index].id;
      // Only record view once per image per session
      if (!viewedImages.current.has(imageId)) {
        viewedImages.current.add(imageId);
        recordImageView.mutate(imageId, {
          onError: (error) => {
            // Silently fail - view tracking is not critical
            console.warn("Failed to record image view:", error);
          },
        });
        // Track in GA4
        trackLightboxView(imageId, galleryId);
      }
    }
  }, [open, index, images, recordImageView, galleryId]);

  // Convert images to lightbox format
  const slides = images.map((image) => ({
    src: image.url,
    alt: image.caption || "",
    width: image.width || 1920,
    height: image.height || 1080,
  }));

  return (
    <Lightbox
      open={open}
      close={onClose}
      slides={slides}
      index={index}
      plugins={[Zoom, Counter]}
      zoom={{
        maxZoomPixelRatio: 3,
        zoomInMultiplier: 2,
        doubleTapDelay: 300,
        doubleClickDelay: 300,
        doubleClickMaxStops: 2,
        keyboardMoveDistance: 50,
        wheelZoomDistanceFactor: 100,
        pinchZoomDistanceFactor: 100,
        scrollToZoom: true,
      }}
      counter={{
        container: { style: { top: "unset", bottom: 0 } },
      }}
      animation={{
        fade: 300,
        swipe: 300,
        easing: {
          fade: "ease",
          swipe: "ease-out",
          navigation: "ease-in-out",
        },
      }}
      controller={{
        closeOnBackdropClick: true,
        closeOnPullDown: true,
        closeOnPullUp: true,
      }}
      carousel={{
        finite: false,
        preload: 2,
        padding: "16px",
        spacing: "30%",
        imageFit: "contain",
      }}
      render={{
        buttonPrev: images.length <= 1 ? () => null : undefined,
        buttonNext: images.length <= 1 ? () => null : undefined,
      }}
      styles={{
        container: { backgroundColor: "rgba(0, 0, 0, 0.95)" },
        button: {
          filter: "none",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
      }}
    />
  );
};
